import React, { useState } from 'react'
import { VIBE_LIST, FEELING_LIST, OCCASION_LIST, getFlavors, Flavor } from '../vibes'
import { Friend } from '../services/friends'
import MiniSignature from './MiniSignature'

interface Props {
  friend: Friend
  onPick: (vibeKey: string, flavor: Flavor) => void
  onClose: () => void
}

function VibeOrb({ color, size = 26 }: { color: string; size?: number }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: `radial-gradient(circle at 35% 32%, #ffffffcc 0%, ${color} 38%, ${color}22 78%, transparent 100%)`,
      boxShadow: `0 0 ${size * 0.7}px ${color}66`,
    }} />
  )
}

export default function SendPanel({ friend, onPick, onClose }: Props) {
  const [chosen, setChosen] = useState<typeof VIBE_LIST[0] | null>(null)
  const [tab, setTab] = useState<'feelings' | 'occasions'>('feelings')

  // lock the page behind the sheet (iOS scroll-through fix)
  React.useEffect(() => {
    const y = window.scrollY
    document.body.style.position = 'fixed'
    document.body.style.top = `-${y}px`
    document.body.style.width = '100%'
    return () => {
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, y)
    }
  }, [])
  const flavors = chosen ? getFlavors(chosen.key) : []

  return (
    <div
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      style={{
        position: 'fixed', inset: 0, zIndex: 500,
        background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 14,
      }}
    >
      <div className="fade-in" style={{
        width: '100%', maxWidth: 420, background: '#101010',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '36px 36px 26px 26px',
        padding: '28px 20px calc(30px + env(safe-area-inset-bottom))',
        maxHeight: '78vh', overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        touchAction: 'pan-y',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 22 }}>
          <div style={{
            width: 46, height: 46, borderRadius: 15,
            background: chosen ? `${chosen.color}22` : 'rgba(0,255,209,0.12)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <VibeOrb color={chosen ? chosen.color : '#00FFD1'} size={20} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="eyebrow">{chosen ? 'choose the flavor' : 'transmitting to'}</div>
            <div className="syne" style={{ fontSize: 20, fontWeight: 800, letterSpacing: '-0.03em' }}>
              {chosen ? chosen.label : friend.label}
            </div>
          </div>
          <button onClick={chosen ? () => setChosen(null) : onClose} style={{
            width: 34, height: 34, borderRadius: '50%',
            background: 'rgba(255,255,255,0.08)', border: 'none',
            color: 'rgba(255,255,255,0.6)', fontSize: 16, cursor: 'pointer',
          }}>{chosen ? '‹' : '✕'}</button>
        </div>

        {!chosen ? (
          <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {(['feelings', 'occasions'] as const).map((t) => (
              <button key={t} onClick={() => setTab(t)} style={{
                flex: 1, padding: '9px 0', borderRadius: 100, cursor: 'pointer',
                fontFamily: 'Syne', fontWeight: 800, fontSize: 11.5,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                border: tab === t ? '1px solid rgba(0,255,209,0.5)' : '1px solid rgba(255,255,255,0.1)',
                background: tab === t ? 'rgba(0,255,209,0.12)' : 'transparent',
                color: tab === t ? 'var(--mint)' : 'rgba(255,255,255,0.5)',
              }}>{t}</button>
            ))}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 9 }}>
            {(tab === 'feelings' ? FEELING_LIST : OCCASION_LIST).map((v) => (
              <button
                key={v.key}
                onClick={() => setChosen(v)}
                style={{
                  position: 'relative', aspectRatio: '1', borderRadius: 20,
                  border: '1px solid rgba(255,255,255,0.1)', background: '#0a0a0a',
                  cursor: 'pointer', overflow: 'hidden',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'flex-end',
                  transition: 'transform 0.12s ease',
                }}
                onPointerDown={(e) => (e.currentTarget.style.transform = 'scale(0.9)')}
                onPointerUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onPointerLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <MiniSignature vibeKey={v.key} color={v.color} />
                <span style={{
                  position: 'relative', zIndex: 2, marginBottom: 7,
                  fontSize: 8.5, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: '0.07em', color: '#fff',
                  fontFamily: 'Space Grotesk',
                  textShadow: '0 1px 4px rgba(0,0,0,0.9)',
                }}>{v.label}</span>
              </button>
            ))}
          </div>
          </>
        ) : (
          <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {flavors.map((f, fi) => {
              const level = fi + 1 // 1 soft, 2 warm, 3 charged
              return (
              <button
                key={f.key}
                onClick={() => onPick(chosen.key, f)}
                style={{
                  padding: '16px 20px', borderRadius: 18,
                  border: `1px solid ${chosen.color}${level === 3 ? '88' : level === 2 ? '55' : '33'}`,
                  background: `${chosen.color}${level === 3 ? '26' : level === 2 ? '18' : '10'}`,
                  boxShadow: level === 3 ? `0 0 24px ${chosen.color}44` : level === 2 ? `0 0 12px ${chosen.color}22` : 'none',
                  cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  transition: 'transform 0.12s ease',
                }}
                onPointerDown={(e) => (e.currentTarget.style.transform = 'scale(0.97)')}
                onPointerUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                onPointerLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
              >
                <span style={{ fontSize: 15, fontWeight: 600, color: '#fff', fontStyle: 'italic' }}>
                  {f.phrase}
                </span>
                <span style={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {[1, 2, 3].map((d) => (
                    <span key={d} style={{
                      width: 5, height: 5, borderRadius: '50%',
                      background: d <= level ? chosen.color : 'rgba(255,255,255,0.15)',
                      boxShadow: d <= level ? `0 0 6px ${chosen.color}` : 'none',
                    }} />
                  ))}
                </span>
              </button>
            )})}
          </div>
        )}

        <div className="eyebrow" style={{ textAlign: 'center', marginTop: 18, fontSize: 9 }}>
          {chosen ? 'one flavor · one second · one feeling' : 'pick a vibe'}
        </div>
      </div>
    </div>
  )
}
